import { Card, CardBody } from '@nextui-org/react';
import {
    checkType,
    CtyButton,
    CtyShowToast,
    NavSidebar,
    formatKeyForDisplay,
    CtySpinner,
    GenericConfigurationFieldRenderer,
    SimpleDeleteConfirmationModal,
    AddConfigurationFieldForm,
    type AddFieldDraft,
} from '@circuitry-ai/doc-ui';
import {
    useCreateAiWorkerConfigurationMutation,
    useUpdateAiWorkerConfigurationMutation,
    useEntityResourcePermissions,
} from '@circuitry-ai/doc-data';
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { deepClone } from './agentutils';
import {
    DEFAULT_ADD_FIELD_DRAFT,
    parseAgentConfigurationSections,
    resolveAddFieldValue,
    resolveConfigId,
    type JsonValue,
} from './agentConfigurationShared';
import { useAgentConfigurationQuery } from './useAgentConfigurationQuery';

export interface AgentConfigurationProps {
    agent?: any;
    configId?: string;
    configVersion?: number | string;
    configVersionId?: string;
    hasConfigurationVersions?: boolean;
    activeVersionConfigurationId?: string;
    onVersionUpdated?: () => void;
}

type DeleteTarget = {
    sectionKey: string;
    updatePath: string;
    deletePath: string;
    configurationId: string;
};
type RenderSectionBodyArgs = {
    aiworkerId?: string;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    allSectionKeys: string[];
    currentSection: string;
    currentDisplayKey: string;
    selectedValue: JsonValue | undefined;
    selectedInitialValue: JsonValue | undefined;
    isPreview: boolean;
    isConfigurationEditable: boolean;
    setSelectedValue: (value: JsonValue) => void;
    setPreviewModeByKey: Dispatch<SetStateAction<Record<string, boolean>>>;
    handleDeleteCurrentField: () => void;
    t: (key: string) => string;
};

const renderAgentSectionBody = ({
    aiworkerId,
    isLoading,
    isFetching,
    isError,
    allSectionKeys,
    currentSection,
    currentDisplayKey,
    selectedValue,
    selectedInitialValue,
    isPreview,
    isConfigurationEditable,
    setSelectedValue,
    setPreviewModeByKey,
    handleDeleteCurrentField,
    t,
}: RenderSectionBodyArgs) => {
    if (!aiworkerId) {
        return <p className="text-sm text-default-500">{t('AI Worker ID is missing for this agent.')}</p>;
    }
    if (isLoading || isFetching) {
        return <CtySpinner size="lg" />;
    }
    if (isError) {
        return <p className="text-sm text-danger">{t('Failed to load actions configuration.')}</p>;
    }
    if (!allSectionKeys.length) {
        return null;
    }
    if (!currentSection) {
        return <p className="text-sm text-default-500">{t('Select a section from the left menu.')}</p>;
    }

    return (
        <GenericConfigurationFieldRenderer
            fieldKey={currentSection}
            fieldLabel={t(formatKeyForDisplay(currentDisplayKey || 'JSON Value'))}
            value={selectedValue}
            initialValue={selectedInitialValue}
            isPreview={isPreview}
            onValueChange={setSelectedValue}
            onPreviewChange={(next) =>
                setPreviewModeByKey((prev) => ({ ...prev, [currentSection]: next }))
            }
            onResetToDefault={() => {
                if (selectedInitialValue !== undefined) {
                    setSelectedValue(selectedInitialValue);
                }
            }}
            onDelete={handleDeleteCurrentField}
            isDisabled={!isConfigurationEditable}
            t={t}
        />
    );
};

export function AgentConfiguration({
    agent,
    configId,
    configVersion,
    configVersionId,
    hasConfigurationVersions = false,
    activeVersionConfigurationId = '',
    onVersionUpdated,
}: Readonly<AgentConfigurationProps>) {
    const { t } = useTranslation();
    const aiworkerId = agent?.aiworker_id ?? '';
    const agentName = agent?.name ?? agent?.aiworker_name ?? 'Agent';
    const { canUpdate } = useEntityResourcePermissions('agent', aiworkerId || undefined);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [currentSection, setCurrentSection] = useState('');
    const [configState, setConfigState] = useState<Record<string, JsonValue>>({});
    const [initialConfig, setInitialConfig] = useState<Record<string, JsonValue>>({});
    const [flatUpdates, setFlatUpdates] = useState<Record<string, JsonValue>>({});
    const [previewModeByKey, setPreviewModeByKey] = useState<Record<string, boolean>>({});
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [pendingDeleteTarget, setPendingDeleteTarget] = useState<DeleteTarget | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [addFieldDraft, setAddFieldDraft] = useState<AddFieldDraft | null>(null);
    const [isAddingConfiguration, setIsAddingConfiguration] = useState(false);
    const [updateConfiguration, { isLoading: isSaving }] = useUpdateAiWorkerConfigurationMutation();
    const [createConfiguration] = useCreateAiWorkerConfigurationMutation();

    const {
        selectedConfiguration,
        selectedConfigurationSignature,
        isLoading,
        isFetching,
        isError,
        isConfigurationEditable,
        refetch,
    } = useAgentConfigurationQuery({
        aiworkerId,
        configId,
        configVersion,
        configVersionId,
        canUpdate,
    });

    const parsedConfiguration = useMemo(
        () => parseAgentConfigurationSections(selectedConfiguration),
        [selectedConfigurationSignature]
    );
    const {
        rootConfigurationId,
        botPathPrefix,
        botDeletePathPrefix,
        botConfigurationId,
    } = parsedConfiguration;
    const requiresCreateConfiguration = !hasConfigurationVersions;
    const patchConfigurationId = botConfigurationId || rootConfigurationId || activeVersionConfigurationId;

    const sectionKeys = useMemo(() => Object.keys(configState), [configState]);
    const allSectionKeys = sectionKeys;
    const allSectionKeysSignature = useMemo(() => JSON.stringify(allSectionKeys), [allSectionKeys]);
    const hasBotSections = sectionKeys.length > 0;
    const showEmptyConfigurationState = !isLoading && !isFetching && !isError && !allSectionKeys.length;

    useEffect(() => {
        const media = globalThis.matchMedia('(max-width: 767px)');
        const handler = () => setIsCollapsed(media.matches);
        handler();
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
        const parsed = parseAgentConfigurationSections(selectedConfiguration);
        setConfigState(deepClone(parsed.botConfig));
        setInitialConfig(deepClone(parsed.botConfig));
        setFlatUpdates({});
        setAddFieldDraft(null);
    }, [selectedConfigurationSignature]);

    useEffect(() => {
        const sectionKeysList: string[] = JSON.parse(allSectionKeysSignature || '[]');
        if (sectionKeysList.length === 0) {
            setCurrentSection((prev) => (prev === '' ? prev : ''));
            return;
        }
        setCurrentSection((prev) => (sectionKeysList.includes(prev) ? prev : sectionKeysList[0]));
    }, [allSectionKeysSignature]);

    const navGroups = useMemo(
        () => [
            {
                heading: null,
                items: [
                    ...sectionKeys.map((key) => ({
                        key,
                        path: `/agents/actions/${key}`,
                        label: t(formatKeyForDisplay(key)),
                        icon: 'mdi:text-box-search-outline',
                    })),
                ],
            },
        ],
        [sectionKeys, t]
    );

    const currentDisplayKey = currentSection;

    const selectedValue: JsonValue | undefined = currentSection ? configState[currentSection] : undefined;
    const selectedInitialValue: JsonValue | undefined = currentSection ? initialConfig[currentSection] : undefined;
    const isPreview = currentSection ? Boolean(previewModeByKey[currentSection]) : false;

    const setSelectedValue = (value: JsonValue) => {
        if (!currentSection || !isConfigurationEditable) return;
        const pathKey = `${botPathPrefix}.${currentSection}`;
        setConfigState((prev) => ({ ...prev, [currentSection]: value }));
        setFlatUpdates((prev) => {
            const next = { ...prev };
            if (JSON.stringify(value) === JSON.stringify(initialConfig[currentSection])) {
                delete next[pathKey];
            } else {
                next[pathKey] = value;
            }
            return next;
        });
    };

    const handleResetAll = () => {
        if (!isConfigurationEditable) return;
        setConfigState(deepClone(initialConfig));
        setFlatUpdates({});
    };

    const handleDeleteCurrentField = () => {
        if (!currentSection || !isConfigurationEditable) return;

        const updatePath = `${botPathPrefix}.${currentSection}`;
        const deletePath = `${botDeletePathPrefix}.${currentSection}`;
        if (!patchConfigurationId) return;
        setPendingDeleteTarget({
            sectionKey: currentSection,
            updatePath,
            deletePath,
            configurationId: patchConfigurationId,
        });
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!pendingDeleteTarget || !isConfigurationEditable) return;
        try {
            setIsDeleting(true);
            await updateConfiguration({
                configurationId: pendingDeleteTarget.configurationId,
                payload: {
                    scope: 'tenant',
                    'deleted.config': [pendingDeleteTarget.deletePath],
                },
            }).unwrap();

            setPreviewModeByKey((prev) => {
                const next = { ...prev };
                delete next[pendingDeleteTarget.sectionKey];
                return next;
            });

            setConfigState((prev) => {
                const next = { ...prev };
                delete next[pendingDeleteTarget.sectionKey];
                return next;
            });
            setInitialConfig((prev) => {
                const next = { ...prev };
                delete next[pendingDeleteTarget.sectionKey];
                return next;
            });
            setFlatUpdates((prev) => {
                const next = { ...prev };
                delete next[pendingDeleteTarget.updatePath];
                return next;
            });
            CtyShowToast(checkType.success, t('Field deleted successfully.'));
            onVersionUpdated?.();
            setPendingDeleteTarget(null);
            setIsDeleteModalOpen(false);
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to delete field.');
            CtyShowToast(checkType.error, String(errorMessage));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSave = async () => {
        if (!isConfigurationEditable) return;
        const hasBotUpdates = Boolean(patchConfigurationId) && Object.keys(flatUpdates).length > 0;
        if (!hasBotUpdates) return;
        try {
            await updateConfiguration({
                configurationId: patchConfigurationId,
                payload: {
                    scope: 'tenant',
                    ...(flatUpdates as Record<string, unknown>),
                },
            }).unwrap();
            setInitialConfig(deepClone(configState));
            setFlatUpdates({});
            CtyShowToast(checkType.success, t('Actions configuration saved successfully.'));
            onVersionUpdated?.();
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to save actions configuration.');
            CtyShowToast(checkType.error, String(errorMessage));
        }
    };

    const handleAddConfiguration = async () => {
        if (!isConfigurationEditable || !aiworkerId || !addFieldDraft) return;

        const trimmedKey = addFieldDraft.keyName.trim();
        if (!trimmedKey) {
            CtyShowToast(checkType.error, t('Key is required.'));
            return;
        }
        if (Object.prototype.hasOwnProperty.call(configState, trimmedKey)) {
            CtyShowToast(checkType.error, t('A field with this key already exists.'));
            return;
        }

        const nextValue = resolveAddFieldValue(addFieldDraft, t);
        if (nextValue === null) return;

        let createConfigId: string | undefined;
        if (requiresCreateConfiguration) {
            const configIdValue = resolveConfigId(addFieldDraft, t);
            if (!configIdValue) return;
            createConfigId = configIdValue;
        } else if (!patchConfigurationId) {
            CtyShowToast(checkType.error, t('Unable to add configuration. Active version configuration ID is missing.'));
            return;
        }

        setIsAddingConfiguration(true);
        try {
            if (requiresCreateConfiguration) {
                await createConfiguration({
                    aiworkerId,
                    agentName,
                    configId: createConfigId!,
                    config: {
                        bot_data: {
                            [trimmedKey]: nextValue,
                        },
                    },
                }).unwrap();
            } else {
                const updatePathKey = `${botPathPrefix}.${trimmedKey}`;
                await updateConfiguration({
                    configurationId: patchConfigurationId,
                    payload: {
                        scope: 'tenant',
                        [updatePathKey]: nextValue,
                    },
                }).unwrap();
            }

            await refetch();
            setAddFieldDraft(null);
            setCurrentSection(trimmedKey);
            onVersionUpdated?.();
            CtyShowToast(checkType.success, t('Configuration added successfully.'));
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to add configuration.');
            CtyShowToast(checkType.error, String(errorMessage));
        } finally {
            setIsAddingConfiguration(false);
        }
    };

    const renderAddConfigurationControls = (variant: 'empty' | 'inline') => {
        if (!isConfigurationEditable) return null;

        if (!addFieldDraft) {
            return (
                <div className={variant === 'empty' ? 'flex flex-col items-center gap-3 py-6' : 'flex justify-end'}>
                    {variant === 'empty' ? (
                        <p className="text-sm text-default-500">{t('No bot_data configuration found for this agent.')}</p>
                    ) : null}
                    <CtyButton
                        size="sm"
                        color="primary"
                        variant="solid"
                        onPress={() => setAddFieldDraft(DEFAULT_ADD_FIELD_DRAFT())}
                        isDisabled={isAddingConfiguration}
                    >
                        {t('Add Configuration')}
                    </CtyButton>
                </div>
            );
        }

        return (
            <AddConfigurationFieldForm
                draft={addFieldDraft}
                isDisabled={!isConfigurationEditable}
                isAdding={isAddingConfiguration}
                showConfigId={requiresCreateConfiguration}
                onDraftChange={(patch) => setAddFieldDraft((prev) => (prev ? { ...prev, ...patch } : prev))}
                onCancel={() => setAddFieldDraft(null)}
                onAdd={handleAddConfiguration}
                t={t}
            />
        );
    };

    return (
        <div className="flex h-full min-h-0 border border-divider overflow-hidden bg-content1">
            <NavSidebar
                navGroups={navGroups}
                currentSection={currentSection}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed((value) => !value)}
                headerTitle='Actions'
                expandMenuLabel={t('Expand menu')}
                collapseMenuLabel={t('Collapse menu')}
                onInternalNavigate={(_, item) => setCurrentSection(item.key)}
                navClassName="pt-0"
            />
            <main className="flex-1 min-w-0 overflow-y-auto p-4 bg-default-50">
                <Card className="border border-divider shadow-none">
                    <CardBody className="gap-3">
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="text-lg font-semibold">{currentSection ? t(formatKeyForDisplay(currentDisplayKey)) : t('Actions')}</h3>
                            <div className="flex items-center gap-2">
                                {hasBotSections ? renderAddConfigurationControls('inline') : null}
                                <CtyButton size="sm" variant="bordered" onPress={handleResetAll} isDisabled={!isConfigurationEditable || !allSectionKeys.length}>
                                    {t('Reset All')}
                                </CtyButton>
                                <CtyButton
                                    size="sm"
                                    color="primary"
                                    onPress={handleSave}
                                    isDisabled={!isConfigurationEditable || !patchConfigurationId || Object.keys(flatUpdates).length === 0}
                                    isLoading={isSaving}
                                >
                                    {t('Save')}
                                </CtyButton>
                            </div>
                        </div>
                        {showEmptyConfigurationState
                            ? renderAddConfigurationControls('empty')
                            : renderAgentSectionBody({
                                aiworkerId,
                                isLoading,
                                isFetching,
                                isError,
                                allSectionKeys,
                                currentSection,
                                currentDisplayKey,
                                selectedValue,
                                selectedInitialValue,
                                isPreview,
                                isConfigurationEditable,
                                setSelectedValue,
                                setPreviewModeByKey,
                                handleDeleteCurrentField,
                                t,
                            })}
                    </CardBody>
                </Card>
            </main>
            <SimpleDeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setIsDeleteModalOpen(false);
                        setPendingDeleteTarget(null);
                    }
                }}
                body={
                    <div className="flex flex-col gap-2">
                        <p className="text-sm text-default-700">
                            {t('Are you sure you want to delete this configuration field?')}
                        </p>
                        {pendingDeleteTarget?.deletePath ? (
                            <p className="text-xs text-default-500">{pendingDeleteTarget.deletePath}</p>
                        ) : null}
                    </div>
                }
                onConfirm={handleConfirmDelete}
                isConfirmLoading={isDeleting}
            />
        </div>
    );
}

export default AgentConfiguration;
